import React from 'react';

import { initializeApp } from 'firebase/app'
import { getAuth, User } from 'firebase/auth'
import { initializeFirestore, persistentLocalCache, persistentMultipleTabManager, doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { ProductFruits } from 'react-product-fruits'
import { Route, Routes, Navigate, useNavigate } from 'react-router-dom'

import LandingPage from 'scenes/LandingPage';
import SignInPage from 'scenes/SignInPage';
import BrandGuidelinesEditor from 'scenes/LandingPage/components/BrandGuidelines';
import BulkTreatment from 'scenes/BulkTreatment';
import TreatmentsHistory from 'scenes/TreatmentsHistory';
import TreatmentDetail from 'scenes/TreatmentDetail';
import AdminPanel from 'scenes/AdminPanel';
import ReleaseNotes from 'scenes/ReleaseNotes';
import Header from 'components/Header';

// Get the Firebase config from the auto generated file.
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_PROJECT_ID
};

const firebaseApp = initializeApp(firebaseConfig)

if (process.env.REACT_APP_ENV === 'local') {
  document.title = 'Dev AI Conic'
} else if (process.env.REACT_APP_ENV === 'staging') {
  document.title = 'Staging AI Conic'
}

export const db = initializeFirestore(
  firebaseApp,
  { localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() })},
  'ai-history'
);

// Protected Route Component
const ProtectedRoute: React.FC<{ 
  element: React.ReactElement; 
  hasAccess: boolean;
  isLoading: boolean;
  redirectTo?: string;
}> = ({ element, hasAccess, isLoading, redirectTo = '/' }) => {
  if (isLoading) {
    return null; // or return a loading spinner
  }
  return hasAccess ? element : <Navigate to={redirectTo} replace />;
};

function App() {
  const navigate = useNavigate();
  const [isSignedIn, setIsSignedIn] = React.useState<boolean | undefined>(undefined)
  const [user, setUser] = React.useState<User | null>(null)
  const [userInfo, setUserInfo] = React.useState<ProductFruitsUserObject>({ username: 'unknown' })
  const [hasAccessToBulkTreatments, setHasAccessToBulkTreatments] = React.useState<boolean>(false)
  const [hasAccessToAiChat, setHasAccessToAiChat] = React.useState<boolean>(false)
  const [isAdmin, setIsAdmin] = React.useState<boolean>(false)
  const [isLoadingPermissions, setIsLoadingPermissions] = React.useState<boolean>(true)

  const createUserInFirestoreIfNeeded = React.useCallback(async (user: User) => {
    if (!user?.uid || !user?.email) return;

    setIsLoadingPermissions(true);
    try {
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        // User doesn't exist in Firestore, create it
        await setDoc(userRef, {
          email: user.email,
          created_at: serverTimestamp(),
          hasAccessToBulkTreatments: false, // Default: no access
          is_admin: false // Default: not admin
        });
        setHasAccessToBulkTreatments(false);
        setHasAccessToAiChat(false);
        setIsAdmin(false);
      } else {
        // User exists, fetch their permissions
        const userData = userDoc.data();
        setHasAccessToBulkTreatments(userData?.bulk_treatments_rights || false);
        setHasAccessToAiChat(userData?.ai_chat_rights || false);
        setIsAdmin(userData?.is_admin || false);
      }
    } catch (error) {
      console.error('Error creating user in Firestore:', error);
      setHasAccessToBulkTreatments(false);
      setHasAccessToAiChat(false);
      setIsAdmin(false);
    } finally {
      setIsLoadingPermissions(false);
    }
  }, []);

  const onAuthStateChange = React.useCallback(() => {
    return getAuth().onAuthStateChanged((user) => {
      setIsSignedIn(!!user)
      setUser(user)
      setUserInfo({
        username: user?.email || 'unknown',
      })
      
      // Create user in Firestore on first login
      if (user) {
        createUserInFirestoreIfNeeded(user);
      } else {
        setIsLoadingPermissions(false);
      }
    })
  }, [createUserInFirestoreIfNeeded])

  React.useEffect(() => {
    const unregisterAuthObserver = onAuthStateChange()
    return () => {
      unregisterAuthObserver()
    }
  }, [onAuthStateChange])

  const handleLogoClick = React.useCallback(() => {
    navigate('/');
  }, [navigate]);

  return (
    <div>
      {process.env.REACT_APP_PRODUCT_FRUITS_WORKSPACE &&
        <ProductFruits workspaceCode={process.env.REACT_APP_PRODUCT_FRUITS_WORKSPACE} language='en'
          user={userInfo}
          config={{
            disableBannersAutoMargin: true
          }}
        />
      }
      { isSignedIn !== undefined && !isSignedIn && <SignInPage firebaseAuth={getAuth()}/>}
      {
        isSignedIn && user &&
          <div>
            <Header onClick={handleLogoClick} isAdmin={isAdmin} />
            <Routes>
              <Route path='/' element={<LandingPage db={db} uid={user.uid} hasAccessToBulkTreatments={hasAccessToBulkTreatments} hasAccessToAiChat={hasAccessToAiChat}/>}/>
              <Route 
                path='/admin' 
                element={
                  <ProtectedRoute 
                    element={<AdminPanel db={db} uid={user.uid}/>} 
                    hasAccess={isAdmin}
                    isLoading={isLoadingPermissions}
                  />
                }
              />
              <Route 
                path='/projects/:project_id/bulk-treatments/new' 
                element={
                  <ProtectedRoute 
                    element={<BulkTreatment db={db} uid={user.uid}/>} 
                    hasAccess={hasAccessToBulkTreatments}
                    isLoading={isLoadingPermissions}
                  />
                }
              />
              <Route 
                path='/projects/:project_id/bulk-treatments' 
                element={
                  <ProtectedRoute 
                    element={<TreatmentsHistory db={db} uid={user.uid}/>} 
                    hasAccess={hasAccessToBulkTreatments}
                    isLoading={isLoadingPermissions}
                  />
                }
              />
              <Route 
                path='/projects/:project_id/bulk-treatments/:treatment_id' 
                element={
                  <ProtectedRoute 
                    element={<TreatmentDetail db={db} uid={user.uid}/>} 
                    hasAccess={hasAccessToBulkTreatments}
                    isLoading={isLoadingPermissions}
                  />
                }
              />
              <Route path='/projects/:project_id' element={<LandingPage db={db} uid={user.uid} hasAccessToBulkTreatments={hasAccessToBulkTreatments} hasAccessToAiChat={hasAccessToAiChat}/>}/>
              <Route path='/projects/:project_id/threads/:thread_id' element={<LandingPage db={db} uid={user.uid} hasAccessToBulkTreatments={hasAccessToBulkTreatments} hasAccessToAiChat={hasAccessToAiChat}/>}/>
              <Route path='/projects/:project_id/threads/:thread_id/videos/:video_id/analysis' element={<LandingPage db={db} uid={user.uid} hasAccessToBulkTreatments={hasAccessToBulkTreatments} hasAccessToAiChat={hasAccessToAiChat}/>}/>
              <Route path='/projects/:project_id/settings' element={<BrandGuidelinesEditor/>}/>
              <Route path='/release-notes' element={<ReleaseNotes/>}/>
              <Route
                path="*"
                element={<Navigate to="/" replace/>}
              />
            </Routes>
          </div>
      }
    </div>
  );
}

export default App;
